import { DateTime } from 'luxon';
import { ShowError } from 'smg/errors';
import { BlockPastDates, ReportValidity } from 'smg/form';
import * as template from 'smg/template';

import i18n from '../i18n';
import { State } from './state';

// SetupAdditional wires the "Additional Details" accordion: event date
// toggle, marketing/SMS/design checkboxes, and the confirm button that
// enables checkout once everything prior is valid.
export function SetupAdditional(state: State): void {
    state.initAdditional = () => initAdditional(state);
    state.updateAdditionalDetailsSummary = () => updateAdditionalDetailsSummary(state);

    bindEventDateToggle(state);
    bindPermissionToggles(state);
    bindAdditionalConfirm(state);
    bindAdditionalCollapse(state);
}

function initAdditional(state: State): void {
    state.dom.$additionalBodyToggle?.classList.add('d-none');
}

function bindEventDateToggle(state: State): void {
    const { dom } = state;
    const revalidateEventDate = BlockPastDates(dom.$eventDate, { earliest: 'tomorrow', message: i18n('dateNotBeforeTomorrow') });
    [dom.$eventDateYes, dom.$eventDateNo].forEach($toggle => {
        $toggle.addEventListener('change', () => {
            if (dom.$eventDateYes.checked) {
                dom.$eventDate.parentElement?.classList.remove('d-none');
                dom.$eventDate.required = true;
                state.additionalDetails.eventDate = DateTime.fromISO(dom.$eventDate.value);
                revalidateEventDate();
            } else {
                dom.$eventDate.parentElement?.classList.add('d-none');
                dom.$eventDate.required = false;
                dom.$eventDate.value = '';
                state.additionalDetails.eventDate = undefined;
                // Hidden, not disabled — clear the past-date message so it
                // can't block the confirm from behind a collapsed field.
                revalidateEventDate();
            }
        });
    });
}

function bindPermissionToggles(state: State): void {
    const { dom } = state;
    dom.$promoApproval.addEventListener('change', () => {
        state.additionalDetails.marketingPermission = dom.$promoApproval.checked;
    });
    dom.$smsApproval.addEventListener('change', () => {
        state.additionalDetails.smsOptIn = dom.$smsApproval.checked;
    });
    dom.$designApproval.addEventListener('change', () => {
        state.additionalDetails.verifiedDesigns = dom.$designApproval.checked;
    });
}

function bindAdditionalConfirm(state: State): void {
    const { dom } = state;
    let submitting = false;
    dom.$additionalBody?.querySelector('#additional-details-confirm')?.addEventListener('click', async () => {
        if (submitting) {
            return;
        }
        if (!ReportValidity(dom.$additionalDetailsForm)) {
            dom.$additionalBodyToggle?.classList.remove('d-none');
            return;
        }

        submitting = true;
        let loadingStarted = false;
        const loadingTimeout = window.setTimeout(() => {
            loadingStarted = true;
            state.startLoading();
        }, 150);
        try {
            state.additionalDetails = {
                eventDate: dom.$eventDate.value ? DateTime.fromISO(dom.$eventDate.value) : undefined,
                marketingPermission: dom.$promoApproval.checked,
                smsOptIn: dom.$smsApproval.checked,
                verifiedDesigns: dom.$designApproval.checked,
            };
            state.detailsComplete = true;
            await state.updateOrderSummary().
                then(() => state.updateShippingSummary()).
                then(() => state.updateTaxExemptionSummary()).
                then(() => state.updateAdditionalDetailsSummary()).
                then(() => {
                    // The last step leaves the customer at the bottom of the page; the summary holds the button they need next.
                    // The step closes, then its preview grows open (a max-height transition); scrolling before both
                    // settle lands short of the summary.
                    dom.$additionalPreview?.addEventListener('transitionend', () => {
                        dom.$orderSummary?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, { once: true });
                    dom.$additionalBodyToggle?.click();
                    state.syncSplitSummary();
                }).catch(ShowError);
        } finally {
            window.clearTimeout(loadingTimeout);
            submitting = false;
            if (loadingStarted) {
                state.stopLoading();
            }
        }
        dom.$additionalBodyToggle?.classList.remove('d-none');
    });
}

function bindAdditionalCollapse(state: State): void {
    const { dom } = state;
    dom.$additionalBodyToggle?.addEventListener('click', () => {
        setTimeout(() => {
            if (!dom.$additionalBodyToggle?.classList.contains('collapsed')) {
                initAdditional(state);
                setTimeout(() => {
                    dom.$additionalBody?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 350);
            }
        });
    });
}

function updateAdditionalDetailsSummary(state: State): void {
    const { dom } = state;
    dom.$additionalPreview?.replaceChildren(template.Clone('#additional-details-summary'));
    const $previewEventDate = dom.$additionalPreview?.querySelector<HTMLDivElement>('#additional-details-preview-section-event-date');
    const $previewPermissions = dom.$additionalPreview?.querySelector<HTMLDivElement>('#additional-details-preview-section-permissions');
    const $previewVerification = dom.$additionalPreview?.querySelector<HTMLDivElement>('#additional-details-preview-section-verification');

    if ($previewEventDate) {
        $previewEventDate.appendChild(renderRow(
            state.additionalDetails?.eventDate !== undefined,
            state.additionalDetails?.eventDate ?
                `I have an event on ${state.additionalDetails.eventDate.toLocaleString()}` :
                'I do not have an event date',
        ));
    }
    if ($previewPermissions) {
        $previewPermissions.appendChild(renderRow(
            Boolean(state.additionalDetails?.marketingPermission),
            state.additionalDetails?.marketingPermission ?
                'I give permission to allow my designs to be used for marketing' :
                'I do not give permission to allow my designs to be used for marketing',
        ));
        $previewPermissions.appendChild(renderRow(
            Boolean(state.additionalDetails?.smsOptIn),
            state.additionalDetails?.smsOptIn ?
                'I would like to receive SMS updates' :
                'I would not like to receive SMS updates',
        ));
    }
    if ($previewVerification) {
        $previewVerification.appendChild(renderRow(
            Boolean(state.additionalDetails?.verifiedDesigns),
            state.additionalDetails?.verifiedDesigns ?
                'I have verified that all colors, content, and spelling are exactly correct' :
                'I have not verified that all colors, content, and spelling are exactly correct',
        ));
    }
}

// renderRow builds one yes/no summary row by cloning the template and
// setting the check/cancel icon, YES/NO label, and descriptive text.
function renderRow(positive: boolean, mainText: string): DocumentFragment {
    const $body = template.Clone('#additional-details-summary-item');
    const $parent = $body.querySelector('.response-short') as HTMLSpanElement;
    const $symbol = $parent.querySelector('.material-symbols-rounded') as HTMLSpanElement;
    const $short = $parent.querySelector('.response-short-text') as HTMLSpanElement;
    const $main = $body.querySelector('.main-text') as HTMLSpanElement;

    $parent.classList.remove(positive ? 'text-red' : 'text-green-800');
    $parent.classList.add(positive ? 'text-green-800' : 'text-red');
    $symbol.textContent = positive ? 'check_circle' : 'cancel';
    $short.textContent = positive ? 'YES' : 'NO';
    $main.textContent = mainText;
    return $body;
}
